import { useMemo, useState } from 'react';
import type { CatalogItem } from '../api';
import { FieldGroup } from '../components/form';
import { Button, Switch } from '../components/ui';
import { findItem, isSelected, setManyItems, toggleItem, updateItem, type Draft } from './draft';

/** Plain words, not R and RW: the person ticking folders is not IT. */
const ACCESS_OPTIONS = [
  { value: 'R' as const, label: 'Read only' },
  { value: 'RW' as const, label: 'Read & write' },
];

/**
 * The on-premise fileshare: 35 folders, each needing read or read/write. Search
 * and bulk actions are not decoration here: without them this step is a wall of
 * toggles and people give up halfway.
 *
 * Folder numbers are the ones on the server, gaps included, so what people see
 * matches what they already say out loud.
 */
export function StepFileshare({
  entries,
  draft,
  onChange,
}: {
  entries: CatalogItem[];
  draft: Draft;
  onChange: (next: Draft) => void;
}) {
  const [query, setQuery] = useState('');

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return entries;
    return entries.filter(
      (entry) => entry.name.toLowerCase().includes(needle) || String(entry.legacyNumber ?? '').includes(needle),
    );
  }, [entries, query]);

  const selected = entries.filter((entry) => isSelected(draft, entry.id));
  const visibleSelected = visible.filter((entry) => isSelected(draft, entry.id));

  function setAccessForSelected(level: 'R' | 'RW') {
    // Applied to what is ticked and visible: a search narrows the bulk action
    // as well as the list, so "set all" never silently touches hidden rows.
    let next = draft;
    for (const entry of visibleSelected) next = updateItem(next, entry.id, { accessLevel: level });
    onChange(next);
  }

  return (
    <FieldGroup
      title="On-premise fileshare"
      description="Tick the folders they need. Each one is read & write unless you switch it to read only. Numbers are the ones on the server; the gaps are folders that have been retired."
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search folders…"
          className="w-full max-w-xs rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 dark:placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        />

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => onChange(setManyItems(draft, visible, true))}>
            Select {query ? 'matching' : 'all'}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => onChange(setManyItems(draft, visible, false))}>
            Clear {query ? 'matching' : 'all'}
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-slate-800/60 dark:text-slate-300">
        <span>
          <strong className="font-semibold">{selected.length}</strong> of {entries.length} folders selected
        </span>
        {visibleSelected.length > 0 && (
          <span className="ml-auto flex items-center gap-2">
            Set {query ? 'matching' : 'all'} selected to
            <Button size="sm" variant="secondary" onClick={() => setAccessForSelected('R')}>
              Read only
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setAccessForSelected('RW')}>
              Read &amp; write
            </Button>
          </span>
        )}
      </div>

      {visible.length === 0 ? (
        <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
          No folder matches “{query}”.
        </p>
      ) : (
        <ul className="grid gap-x-8 sm:grid-cols-2">
          {visible.map((entry) => {
            const on = isSelected(draft, entry.id);
            const item = findItem(draft, entry.id);
            const level = item?.accessLevel ?? 'RW';

            return (
              <li
                key={entry.id}
                className="flex items-center gap-3 border-b border-slate-100 py-2.5 dark:border-slate-800"
              >
                <Switch
                  id={`fileshare-${entry.id}`}
                  checked={on || entry.isCompulsory}
                  disabled={entry.isCompulsory}
                  onChange={(next) => onChange(toggleItem(draft, entry, next))}
                  label={entry.name}
                />

                <span className="w-7 shrink-0 text-right text-xs tabular-nums text-slate-500 dark:text-slate-400">
                  {entry.legacyNumber ?? ''}
                </span>

                <label
                  htmlFor={`fileshare-${entry.id}`}
                  className={
                    on
                      ? 'min-w-0 flex-1 cursor-pointer truncate text-sm text-slate-800 dark:text-slate-200'
                      : 'min-w-0 flex-1 cursor-pointer truncate text-sm text-slate-500 dark:text-slate-500'
                  }
                >
                  {entry.name}
                </label>

                {on && (
                  <span
                    role="group"
                    aria-label={`Access to ${entry.name}`}
                    className="flex shrink-0 overflow-hidden rounded-lg border border-slate-300 dark:border-slate-700"
                  >
                    {ACCESS_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        aria-pressed={level === option.value}
                        onClick={() => onChange(updateItem(draft, entry.id, { accessLevel: option.value }))}
                        className={
                          level === option.value
                            ? 'press px-3 py-1.5 text-sm font-semibold text-white ' +
                              (option.value === 'RW' ? 'bg-copper-600 dark:bg-copper-500' : 'bg-slate-700 dark:bg-slate-600')
                            : 'press bg-white px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800'
                        }
                      >
                        {option.label}
                      </button>
                    ))}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
        <strong className="font-medium">Read & write</strong> lets them change files in the folder, and is what
        most people need, so it is the default. <strong className="font-medium">Read only</strong> is for looking,
        not changing.
      </p>
    </FieldGroup>
  );
}
